import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import Throbber from "../throbber";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive active:scale-95 hover:cursor-pointer disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  isLoading = false,
  asChild = false,
  children,
  style,
  ref: forwardedRef,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    isLoading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";
  const elementRef = React.useRef<HTMLButtonElement | null>(null);
  const idleSize = React.useRef<{ width: number; height: number } | null>(null);

  // Call sites swap the label for <Throbber /> while pending; treat that the
  // same as `isLoading` so the button can hold its size.
  const loading =
    isLoading || (React.isValidElement(children) && children.type === Throbber);

  // Remember the size the button had while idle, then pin it while the
  // spinner replaces the label. Otherwise the button shrinks to the spinner's
  // width and any flex-grow sibling expands to fill the gap.
  React.useLayoutEffect(() => {
    const el = elementRef.current;
    if (!loading && el) {
      idleSize.current = { width: el.offsetWidth, height: el.offsetHeight };
    }
  });

  const setRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      elementRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  return (
    <Comp
      ref={setRef}
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      style={
        loading && idleSize.current
          ? {
              minWidth: idleSize.current.width,
              minHeight: idleSize.current.height,
              ...style,
            }
          : style
      }
      {...props}
    >
      {isLoading ? <Throbber /> : children}
    </Comp>
  );
}

export { Button, buttonVariants };
